import { defineStore } from "pinia";
import { ref } from "vue";
import { companyOrderApi } from "./companyOrderApi";
import {
  CompanyOrder,
  CompanyOrderUpdatePayload,
  OrderStatus,
} from "./companyOrderTypes";

export const useCompanyOrderStore = defineStore("companyOrder", () => {
  const orders = ref<CompanyOrder[]>([]);
  const selectedOrder = ref<CompanyOrder | null>(null);
  const isLoading = ref(false);
  const isConfirming = ref<number | null>(null);
  const isUpdatingStatus = ref<number | null>(null);
  const isBatchProcessing = ref(false);
  const batchProcessingOrderId = ref<number | null>(null);
  let ordersRequestSequence = 0;

  const fetchOrders = async (
    companyIds: number[],
    dateFrom: string | null = null,
    dateBefore: string | null = null
  ) => {
    if (companyIds.length === 0) {
      ordersRequestSequence++;
      orders.value = [];
      isLoading.value = false;
      return;
    }
    await loadLatestOrders(() =>
      companyOrderApi.listCompanyOrders({
        company_ids: companyIds,
        date_from: dateFrom,
        date_before: dateBefore,
      })
    );
  };

  const fetchAdminNonCompletedOrders = (
    dateFrom: string | null = null,
    dateBefore: string | null = null
  ) =>
    loadLatestOrders(() =>
      companyOrderApi.listAdminNonCompletedCompanyOrders({
        date_from: dateFrom,
        date_before: dateBefore,
      })
    );

  const loadLatestOrders = async (
    request: () => Promise<{ data: { orders: CompanyOrder[] } }>
  ) => {
    const sequence = ++ordersRequestSequence;
    isLoading.value = true;
    try {
      const response = await request();
      // NOTE: companies and dates can change while a request is in flight; only the latest may land.
      if (sequence === ordersRequestSequence)
        orders.value = response.data.orders;
    } finally {
      if (sequence === ordersRequestSequence) isLoading.value = false;
    }
  };

  const updateOrderWithStatus = async (
    orderId: number,
    apiCall: () => Promise<any>,
    newStatus: OrderStatus
  ) => {
    isConfirming.value = orderId;
    try {
      await apiCall();
      orders.value = orders.value.map((order: CompanyOrder) =>
        order.id === orderId ? { ...order, status: newStatus } : order
      );
    } finally {
      isConfirming.value = null;
    }
  };

  const confirmOrder = async (orderId: number) => {
    return updateOrderWithStatus(
      orderId,
      () => companyOrderApi.confirmOrder(orderId),
      OrderStatus.PENDING
    );
  };

  const cancelOrder = async (orderId: number) => {
    return updateOrderWithStatus(
      orderId,
      () => companyOrderApi.cancelOrder(orderId),
      OrderStatus.CANCELLED
    );
  };

  const updateOrder = async (
    orderId: number,
    payload: CompanyOrderUpdatePayload
  ) => {
    isUpdatingStatus.value = orderId;
    try {
      const response = await companyOrderApi.updateOrder(orderId, payload);
      // The PUT response nests order_details next to the order, not inside it —
      // merge them back so list rows keep e.g. the cancellation purpose for confirm.
      const updatedOrder: CompanyOrder = {
        ...response.data.order,
        order_details: response.data.order_details,
      };
      orders.value = orders.value.map((order: CompanyOrder) =>
        order.id === orderId ? updatedOrder : order
      );
      if (selectedOrder.value?.id === orderId) {
        selectedOrder.value = updatedOrder;
      }
      return response.data;
    } finally {
      isUpdatingStatus.value = null;
    }
  };

  const adminUpdateStatus = async (
    orderId: number,
    status: "COMPLETED" | "FAILED" | "CANCELLED"
  ) => {
    isUpdatingStatus.value = orderId;
    try {
      await companyOrderApi.adminUpdateStatus(orderId, status);
      orders.value = orders.value.map((order: CompanyOrder) =>
        order.id === orderId
          ? { ...order, status: status as OrderStatus }
          : order
      );
    } finally {
      isUpdatingStatus.value = null;
    }
  };

  const adminRetryOrder = async (orderId: number) => {
    isUpdatingStatus.value = orderId;
    try {
      await companyOrderApi.adminRetryOrder(orderId);
      orders.value = orders.value.map((order: CompanyOrder) =>
        order.id === orderId ? { ...order, status: OrderStatus.PENDING } : order
      );
    } finally {
      isUpdatingStatus.value = null;
    }
  };

  const updateOrderStatus = (orderId: number, status: OrderStatus) => {
    orders.value = orders.value.map((order: CompanyOrder) =>
      order.id === orderId ? { ...order, status } : order
    );
  };

  const setSelectedOrder = (order: CompanyOrder | null) => {
    selectedOrder.value = order;
  };

  const processBatchOrders = async (
    visibleOrders: CompanyOrder[],
    processOrder: (order: CompanyOrder) => Promise<void>,
    errorLogPrefix: string
  ) => {
    const waitingOrders = visibleOrders.filter(
      (order) => order.status === OrderStatus.WAITING_CONFIRMATION
    );

    if (waitingOrders.length === 0) {
      return { success: 0, failed: 0 };
    }

    isBatchProcessing.value = true;
    let successCount = 0;
    let failedCount = 0;

    for (const order of waitingOrders) {
      try {
        batchProcessingOrderId.value = order.id;
        await processOrder(order);
        successCount++;
      } catch (err) {
        failedCount++;
        console.error(`${errorLogPrefix} ${order.id}:`, err);
      }
    }

    batchProcessingOrderId.value = null;
    isBatchProcessing.value = false;

    return { success: successCount, failed: failedCount };
  };

  const confirmAllWaitingOrders = async (visibleOrders: CompanyOrder[]) => {
    return processBatchOrders(
      visibleOrders,
      (order) => confirmOrder(order.id),
      "Failed to confirm order"
    );
  };

  const cancelAllWaitingOrders = async (visibleOrders: CompanyOrder[]) => {
    return processBatchOrders(
      visibleOrders,
      (order) => cancelOrder(order.id),
      "Failed to cancel order"
    );
  };

  const rejectAllWaitingOrders = async (visibleOrders: CompanyOrder[]) => {
    return processBatchOrders(
      visibleOrders,
      (order) => adminUpdateStatus(order.id, "CANCELLED"),
      "Failed to reject order"
    );
  };

  return {
    orders,
    selectedOrder,
    isLoading,
    isConfirming,
    isUpdatingStatus,
    isBatchProcessing,
    batchProcessingOrderId,
    fetchOrders,
    fetchAdminNonCompletedOrders,
    confirmOrder,
    cancelOrder,
    updateOrder,
    adminUpdateStatus,
    adminRetryOrder,
    updateOrderStatus,
    setSelectedOrder,
    confirmAllWaitingOrders,
    cancelAllWaitingOrders,
    rejectAllWaitingOrders,
  };
});
